/**
 * The demo client-report document (served raw as text/html for
 * `GET client-reports/{id}/html`). It renders inside the workbench's sandboxed
 * viewer iframe, so it is deliberately SCRIPT-FREE — the injected viewer runtime
 * supplies the interactivity:
 *   - `<section class="slide">` blocks become full-page slides you page through
 *     (click zones / arrow keys / scroll-snap; the workbench shows "‹ n/N ›");
 *   - `<a href="#id">` links jump to the slide containing that id (clickable TOC);
 *   - `[data-expandable][data-expand-label]` blocks become animated collapsibles
 *     (drill-downs) — `deep_dive` is on in the workbench;
 *   - `.card` elements reveal on scroll.
 * CSP allows inline <style> + https/data images only (no author scripts).
 */

// A self-contained, premium dark performance report: cover → overview →
// Google Ads → Meta Ads → TikTok → recommendations.
export const DEMO_REPORT_HTML = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>Acme B.V. — Performance Report — June 2026</title>
<style>
  :root{
    --bg:#0b1020; --bg2:#0e1430; --panel:rgba(255,255,255,.045);
    --panel2:rgba(255,255,255,.07); --line:rgba(255,255,255,.12);
    --ink:#eef2ff; --muted:#9aa6c4; --faint:#6b7699;
    --accent:#ffd84d; --pos:#34d399; --neg:#fb7185;
    --google:#5b9bff; --meta:#4f8cff; --tiktok:#ff4d6d; --tiktok2:#25f4ee;
    /* runtime expand-toggle theming */
    --r-accent:#ffd84d; --r-line:rgba(255,255,255,.14); --r-panel:rgba(255,255,255,.04);
  }
  *{box-sizing:border-box}
  html,body{margin:0;padding:0}
  body{
    background:radial-gradient(1200px 700px at 80% -10%, #17204a 0%, var(--bg) 55%) fixed;
    color:var(--ink);
    font:15px/1.55 -apple-system,'Segoe UI',Roboto,Inter,system-ui,sans-serif;
    -webkit-font-smoothing:antialiased;
  }
  .slide{
    min-height:100vh; scroll-snap-align:start;
    display:flex; flex-direction:column; justify-content:center;
    padding:6vh clamp(20px,6vw,72px);
  }
  .wrap{width:100%; max-width:1000px; margin:0 auto}
  h1,h2,h3,h4{margin:0; font-weight:700; letter-spacing:-.01em}
  .eyebrow{font-size:12px; letter-spacing:.16em; text-transform:uppercase; color:var(--accent); font-weight:700}
  .muted{color:var(--muted)}
  .faint{color:var(--faint)}
  a{color:var(--accent); text-decoration:none}

  /* cover */
  .cover-brand{display:flex; align-items:center; gap:14px; font-weight:700; font-size:15px; letter-spacing:.02em}
  .dot{width:10px;height:10px;border-radius:50%;background:var(--accent);box-shadow:0 0 18px rgba(255,216,77,.6)}
  .cover h1{font-size:clamp(34px,6vw,64px); line-height:1.02; margin:18px 0 8px}
  .cover .sub{font-size:clamp(15px,2vw,19px); color:var(--muted)}
  .hero{display:grid; grid-template-columns:repeat(3,1fr); gap:14px; margin:34px 0 30px}
  .hero .card{padding:20px 20px 18px}
  .hero .big{font-size:clamp(26px,3.4vw,40px); font-weight:800; letter-spacing:-.02em; line-height:1}
  .toc{display:flex; flex-wrap:wrap; gap:10px}
  .toc a{display:inline-flex; align-items:center; gap:8px; padding:9px 15px; border:1px solid var(--line);
    border-radius:999px; color:var(--ink); font-weight:600; font-size:13.5px; background:var(--panel)}
  .toc a:hover{border-color:var(--accent); color:var(--accent)}
  .toc .n{color:var(--faint); font-variant-numeric:tabular-nums}

  /* cards + kpis */
  .card{border:1px solid var(--line); background:var(--panel); border-radius:16px}
  .kgrid{display:grid; grid-template-columns:repeat(4,1fr); gap:12px}
  .kpi{padding:16px 16px 14px}
  .kpi .lab{font-size:12px; color:var(--muted); font-weight:600; letter-spacing:.02em}
  .kpi .val{font-size:26px; font-weight:800; letter-spacing:-.02em; margin-top:6px; line-height:1}
  .chip{display:inline-flex; align-items:center; gap:4px; margin-top:9px; font-size:12px; font-weight:700;
    padding:3px 8px; border-radius:999px}
  .chip.up{color:var(--pos); background:rgba(52,211,153,.12)}
  .chip.down{color:var(--neg); background:rgba(251,113,133,.12)}
  .chip.flat{color:var(--muted); background:rgba(255,255,255,.06)}

  /* section headers */
  .shead{display:flex; align-items:center; gap:14px; margin-bottom:6px}
  .badge{width:40px;height:40px;border-radius:11px;display:flex;align-items:center;justify-content:center;
    font-weight:800;font-size:16px;color:#0b1020;flex:none}
  .b-g{background:linear-gradient(135deg,#8ab4ff,#5b9bff)}
  .b-m{background:linear-gradient(135deg,#6ea8ff,#4f8cff)}
  .b-t{background:linear-gradient(135deg,#25f4ee,#ff4d6d)}
  .b-y{background:linear-gradient(135deg,#ffe58a,#ffd84d)}
  .shead h2{font-size:clamp(22px,3vw,30px)}
  .lead{color:var(--muted); font-size:15.5px; max-width:70ch; margin:14px 0 22px}

  /* tables */
  table{width:100%; border-collapse:collapse; font-size:13.5px}
  thead th{text-align:right; color:var(--faint); font-weight:600; font-size:11.5px; letter-spacing:.06em;
    text-transform:uppercase; padding:0 12px 10px; border-bottom:1px solid var(--line)}
  thead th:first-child{text-align:left}
  tbody td{padding:12px; border-bottom:1px solid rgba(255,255,255,.06); text-align:right; font-variant-numeric:tabular-nums}
  tbody td:first-child{text-align:left; font-weight:600; color:var(--ink)}
  tbody tr:last-child td{border-bottom:0}
  .pos{color:var(--pos)} .neg{color:var(--neg)}
  .tag{font-size:11px; font-weight:700; padding:2px 7px; border-radius:6px; background:rgba(255,255,255,.07); color:var(--muted)}

  /* charts */
  .chartcard{padding:18px 18px 8px; margin:0 0 16px}
  .chart-h{display:flex; justify-content:space-between; align-items:baseline; margin-bottom:6px}
  .legend{display:flex; gap:16px; font-size:12px; color:var(--muted)}
  .legend i{display:inline-block;width:9px;height:9px;border-radius:2px;margin-right:6px;vertical-align:middle}
  svg{display:block; width:100%; height:auto}
  .grid2{display:grid; grid-template-columns:1.4fr 1fr; gap:16px}
  @media (max-width:820px){.kgrid{grid-template-columns:repeat(2,1fr)}.hero{grid-template-columns:1fr}.grid2{grid-template-columns:1fr}}

  /* recommendations */
  .recs{display:grid; grid-template-columns:1fr 1fr; gap:14px}
  .rec{padding:18px 20px}
  .rec .n{font-size:12px;font-weight:800;color:var(--accent);letter-spacing:.04em}
  .rec h4{font-size:16px; margin:8px 0 6px}
  .rec p{margin:0; color:var(--muted); font-size:13.5px}
  @media (max-width:820px){.recs{grid-template-columns:1fr}}

  /* reveal (runtime adds body.anim then .reveal on scroll; safe default: visible) */
  body.anim .card:not(.reveal){opacity:0; transform:translateY(12px)}
  .card{transition:opacity .5s ease-out, transform .5s ease-out, border-color .2s}
  .pager{position:fixed; right:18px; bottom:16px; font-size:12px; color:var(--faint);
    background:rgba(11,16,32,.6); border:1px solid var(--line); padding:6px 11px; border-radius:999px}
</style>
</head>
<body>

<!-- ============ 1 · COVER ============ -->
<section class="slide cover" id="cover">
  <div class="wrap">
    <div class="cover-brand"><span class="dot"></span> AdUp <span class="faint">×</span> Acme&nbsp;B.V.</div>
    <div class="eyebrow" style="margin-top:26px">Monthly performance report</div>
    <h1>Paid growth,<br/>at a glance.</h1>
    <div class="sub">Acme B.V. · 1&ndash;30 June 2026 · all channels</div>

    <div class="hero">
      <div class="card"><div class="kpi"><div class="lab">Blended ROAS</div><div class="big">4.20×</div><span class="chip up">▲ 18% vs May</span></div></div>
      <div class="card"><div class="kpi"><div class="lab">Revenue</div><div class="big">€248.5k</div><span class="chip up">▲ 23%</span></div></div>
      <div class="card"><div class="kpi"><div class="lab">Ad spend</div><div class="big">€59.2k</div><span class="chip up">▲ 6%</span></div></div>
    </div>

    <div class="toc">
      <a href="#overview"><span class="n">01</span> Overview</a>
      <a href="#gads"><span class="n">02</span> Google Ads</a>
      <a href="#meta"><span class="n">03</span> Meta Ads</a>
      <a href="#tiktok"><span class="n">04</span> TikTok Ads</a>
      <a href="#recs"><span class="n">05</span> Recommendations</a>
    </div>
    <div class="faint" style="margin-top:22px;font-size:12.5px">Use the arrow keys, scroll, or click the top / bottom of a slide to page through.</div>
  </div>
</section>

<!-- ============ 2 · OVERVIEW ============ -->
<section class="slide" id="overview">
  <div class="wrap">
    <div class="eyebrow">01 — Overview</div>
    <h2 style="font-size:clamp(22px,3vw,30px);margin:8px 0 4px">The month in numbers</h2>
    <p class="lead">A strong June: revenue up <b>23%</b> on a <b>6%</b> spend increase, so efficiency improved across the board. Google Shopping and Meta Advantage+ did the heavy lifting; TikTok kept scaling profitably on fresh UGC.</p>

    <div class="kgrid">
      <div class="card"><div class="kpi"><div class="lab">Conversions</div><div class="val">2,740</div><span class="chip up">▲ 15%</span></div></div>
      <div class="card"><div class="kpi"><div class="lab">CPA</div><div class="val">€21.6</div><span class="chip up">▼ 8%</span></div></div>
      <div class="card"><div class="kpi"><div class="lab">AOV</div><div class="val">€90.7</div><span class="chip up">▲ 7%</span></div></div>
      <div class="card"><div class="kpi"><div class="lab">CTR</div><div class="val">4.3%</div><span class="chip up">▲ 0.4pt</span></div></div>
    </div>

    <div class="grid2" style="margin-top:16px">
      <div class="card chartcard">
        <div class="chart-h"><h4 style="font-size:14px">Spend vs. revenue — weekly</h4>
          <div class="legend"><span><i style="background:var(--accent)"></i>Revenue</span><span><i style="background:var(--google)"></i>Spend</span></div></div>
        <svg viewBox="0 0 620 220" preserveAspectRatio="none" role="img" aria-label="Spend versus revenue by week">
          <g stroke="rgba(255,255,255,.08)" stroke-width="1">
            <line x1="40" y1="30" x2="600" y2="30"/><line x1="40" y1="80" x2="600" y2="80"/>
            <line x1="40" y1="130" x2="600" y2="130"/><line x1="40" y1="180" x2="600" y2="180"/>
          </g>
          <path d="M40 150 L200 132 L360 104 L520 70 L600 60 L600 180 L40 180 Z" fill="rgba(255,216,77,.14)"/>
          <path d="M40 150 L200 132 L360 104 L520 70 L600 60" fill="none" stroke="var(--accent)" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M40 176 L200 172 L360 168 L520 165 L600 164" fill="none" stroke="var(--google)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" opacity=".9"/>
          <g fill="var(--faint)" font-size="11" text-anchor="middle">
            <text x="120" y="205">Wk 1</text><text x="280" y="205">Wk 2</text><text x="440" y="205">Wk 3</text><text x="560" y="205">Wk 4</text></g>
        </svg>
      </div>
      <div class="card chartcard">
        <div class="chart-h"><h4 style="font-size:14px">Revenue by channel</h4><span class="faint" style="font-size:12px">share of €248.5k</span></div>
        <svg viewBox="0 0 300 220" role="img" aria-label="Revenue share by channel">
          <g font-size="12" fill="var(--ink)">
            <text x="0" y="34">Google Ads</text><text x="0" y="98">Meta Ads</text><text x="0" y="162">TikTok Ads</text>
          </g>
          <g>
            <rect x="0" y="44" width="300" height="12" rx="6" fill="rgba(255,255,255,.08)"/>
            <rect x="0" y="44" width="286" height="12" rx="6" fill="var(--google)"/>
            <rect x="0" y="108" width="300" height="12" rx="6" fill="rgba(255,255,255,.08)"/>
            <rect x="0" y="108" width="208" height="12" rx="6" fill="var(--meta)"/>
            <rect x="0" y="172" width="300" height="12" rx="6" fill="rgba(255,255,255,.08)"/>
            <rect x="0" y="172" width="106" height="12" rx="6" fill="var(--tiktok)"/>
          </g>
          <g font-size="12" font-weight="700" fill="var(--muted)" text-anchor="end">
            <text x="300" y="34">€118.4k · 48%</text><text x="300" y="98">€86.2k · 35%</text><text x="300" y="162">€43.9k · 18%</text></g>
        </svg>
      </div>
    </div>
  </div>
</section>

<!-- ============ 3 · GOOGLE ADS ============ -->
<section class="slide" id="gads">
  <div class="wrap">
    <div class="shead"><span class="badge b-g">G</span><div><div class="eyebrow" style="color:var(--google)">02 — Channel</div><h2>Google Ads</h2></div></div>
    <p class="lead">Shopping is the profit engine (4.6× ROAS at scale) and brand search stays near-free at 8.1×. Non-brand search is the one drag — see the keyword drill-down for where budget is leaking.</p>

    <div class="kgrid">
      <div class="card"><div class="kpi"><div class="lab">Spend</div><div class="val">€26.8k</div><span class="chip up">▲ 9%</span></div></div>
      <div class="card"><div class="kpi"><div class="lab">Revenue</div><div class="val">€118.4k</div><span class="chip up">▲ 21%</span></div></div>
      <div class="card"><div class="kpi"><div class="lab">ROAS</div><div class="val">4.42×</div><span class="chip up">▲ 11%</span></div></div>
      <div class="card"><div class="kpi"><div class="lab">Conversions</div><div class="val">1,240</div><span class="chip up">▲ 14%</span></div></div>
    </div>

    <div class="card chartcard" style="margin-top:16px">
      <div class="chart-h"><h4 style="font-size:14px">Top campaigns</h4><span class="faint" style="font-size:12px">June 2026</span></div>
      <table>
        <thead><tr><th>Campaign</th><th>Spend</th><th>ROAS</th><th>Conv.</th><th>CPA</th><th>Δ ROAS</th></tr></thead>
        <tbody>
          <tr><td>Brand — Search <span class="tag">brand</span></td><td>€4,200</td><td>8.10×</td><td>310</td><td>€13.5</td><td class="pos">▲ 6%</td></tr>
          <tr><td>Shopping — Core</td><td>€9,400</td><td>4.60×</td><td>480</td><td>€19.6</td><td class="pos">▲ 12%</td></tr>
          <tr><td>Performance Max — Prospecting</td><td>€7,600</td><td>3.40×</td><td>300</td><td>€25.3</td><td class="pos">▲ 5%</td></tr>
          <tr><td>Non-brand — Search</td><td>€5,600</td><td>3.00×</td><td>150</td><td>€37.3</td><td class="neg">▼ 4%</td></tr>
        </tbody>
      </table>
    </div>

    <div data-expandable data-expand-label="Drill down — top search terms">
      <div class="card chartcard">
        <table>
          <thead><tr><th>Search term</th><th>Cost</th><th>Conv.</th><th>ROAS</th><th>Note</th></tr></thead>
          <tbody>
            <tr><td>acme running shoes</td><td>€1,180</td><td>96</td><td>5.10×</td><td class="faint">brand + category</td></tr>
            <tr><td>lightweight trail sneakers</td><td>€980</td><td>41</td><td>3.20×</td><td class="faint">scaling</td></tr>
            <tr><td>best cushioned running shoes</td><td>€860</td><td>28</td><td>2.70×</td><td class="faint">watch CPA</td></tr>
            <tr><td>cheap sneakers online</td><td>€640</td><td>7</td><td>0.90×</td><td class="neg">add as negative</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</section>

<!-- ============ 4 · META ADS ============ -->
<section class="slide" id="meta">
  <div class="wrap">
    <div class="shead"><span class="badge b-m">M</span><div><div class="eyebrow" style="color:var(--meta)">03 — Channel</div><h2>Meta Ads</h2></div></div>
    <p class="lead">Advantage+ Shopping now drives the bulk of Meta revenue, and DPA retargeting is the efficiency star at 7.9×. Prospecting ROAS is soft — the creative drill-down shows UGC is the way out.</p>

    <div class="kgrid">
      <div class="card"><div class="kpi"><div class="lab">Spend</div><div class="val">€21.4k</div><span class="chip up">▲ 4%</span></div></div>
      <div class="card"><div class="kpi"><div class="lab">Revenue</div><div class="val">€86.2k</div><span class="chip up">▲ 26%</span></div></div>
      <div class="card"><div class="kpi"><div class="lab">ROAS</div><div class="val">4.03×</div><span class="chip up">▲ 21%</span></div></div>
      <div class="card"><div class="kpi"><div class="lab">Frequency</div><div class="val">2.3</div><span class="chip flat">= steady</span></div></div>
    </div>

    <div class="card chartcard" style="margin-top:16px">
      <div class="chart-h"><h4 style="font-size:14px">Top campaigns</h4><span class="faint" style="font-size:12px">June 2026</span></div>
      <table>
        <thead><tr><th>Campaign</th><th>Spend</th><th>ROAS</th><th>Conv.</th><th>CTR</th><th>Δ ROAS</th></tr></thead>
        <tbody>
          <tr><td>Advantage+ Shopping</td><td>€8,900</td><td>4.70×</td><td>430</td><td>2.4%</td><td class="pos">▲ 18%</td></tr>
          <tr><td>Retargeting — DPA</td><td>€3,600</td><td>7.90×</td><td>250</td><td>3.1%</td><td class="pos">▲ 9%</td></tr>
          <tr><td>Prospecting — Lookalike 3%</td><td>€6,300</td><td>2.80×</td><td>210</td><td>1.6%</td><td class="neg">▼ 6%</td></tr>
          <tr><td>Creative test — UGC</td><td>€2,600</td><td>3.10×</td><td>90</td><td>2.9%</td><td class="pos">▲ new</td></tr>
        </tbody>
      </table>
    </div>

    <div data-expandable data-expand-label="Drill down — creative performance">
      <div class="card chartcard">
        <table>
          <thead><tr><th>Creative</th><th>Format</th><th>Spend</th><th>CTR</th><th>ROAS</th></tr></thead>
          <tbody>
            <tr><td>Summer Drop — UGC</td><td>Reel · 9:16 video</td><td>€3,100</td><td>2.8%</td><td>5.20×</td></tr>
            <tr><td>Bestsellers carousel</td><td>Carousel</td><td>€2,250</td><td>1.9%</td><td>4.40×</td></tr>
            <tr><td>Founder story</td><td>Reel · 9:16 video</td><td>€1,700</td><td>3.4%</td><td>3.20×</td></tr>
            <tr><td>Static — product hero</td><td>Single image</td><td>€1,050</td><td>1.1%</td><td>2.10×</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</section>

<!-- ============ 5 · TIKTOK ADS ============ -->
<section class="slide" id="tiktok">
  <div class="wrap">
    <div class="shead"><span class="badge b-t">T</span><div><div class="eyebrow" style="color:var(--tiktok)">04 — Channel</div><h2>TikTok Ads</h2></div></div>
    <p class="lead">TikTok scaled 32% in spend and stayed profitable at ~4× — hook rate on the top Spark Ad is an excellent 28%. Creative fatigue is starting on the hero ad; queue two fresh UGC concepts for July.</p>

    <div class="kgrid">
      <div class="card"><div class="kpi"><div class="lab">Spend</div><div class="val">€11.0k</div><span class="chip up">▲ 32%</span></div></div>
      <div class="card"><div class="kpi"><div class="lab">Revenue</div><div class="val">€43.9k</div><span class="chip up">▲ 29%</span></div></div>
      <div class="card"><div class="kpi"><div class="lab">ROAS</div><div class="val">3.99×</div><span class="chip down">▼ 2%</span></div></div>
      <div class="card"><div class="kpi"><div class="lab">Hook rate</div><div class="val">28%</div><span class="chip up">▲ 3pt</span></div></div>
    </div>

    <div class="card chartcard" style="margin-top:16px">
      <table>
        <thead><tr><th>Campaign</th><th>Spend</th><th>ROAS</th><th>Conv.</th><th>Hook</th></tr></thead>
        <tbody>
          <tr><td>Spark Ads — Creator UGC</td><td>€5,200</td><td>4.50×</td><td>270</td><td>28%</td></tr>
          <tr><td>Prospecting — Video Shopping</td><td>€3,800</td><td>3.60×</td><td>170</td><td>22%</td></tr>
          <tr><td>Retargeting — Web visitors</td><td>€2,000</td><td>3.90×</td><td>80</td><td>19%</td></tr>
        </tbody>
      </table>
    </div>
    <p class="faint" style="font-size:12.5px;margin-top:14px">LinkedIn Ads is connected but paused this period — no spend in June.</p>
  </div>
</section>

<!-- ============ 6 · RECOMMENDATIONS ============ -->
<section class="slide" id="recs">
  <div class="wrap">
    <div class="shead"><span class="badge b-y">→</span><div><div class="eyebrow">05 — Next steps</div><h2>Recommendations for July</h2></div></div>
    <p class="lead">Four moves TARA has queued as proposals for your approval — each is already scoped against your ROAS floor of 3.5×.</p>

    <div class="recs">
      <div class="card rec"><div class="n">01 · SCALE</div><h4>+20% budget → Google Shopping — Core</h4><p>Sitting at 4.6× ROAS with impression share head-room. Projected +€9k revenue at held efficiency.</p></div>
      <div class="card rec"><div class="n">02 · SHIFT</div><h4>Move 15% of Meta prospecting into Advantage+</h4><p>Lookalike prospecting is at 2.8×; Advantage+ Shopping is at 4.7×. Reallocating lifts blended Meta ROAS to ~4.4×.</p></div>
      <div class="card rec"><div class="n">03 · REFRESH</div><h4>Queue 2 new TikTok UGC concepts</h4><p>Hook rate is strong (28%) but the hero ad is fatiguing. Fresh creative keeps the channel scaling into July.</p></div>
      <div class="card rec"><div class="n">04 · FIX</div><h4>Add negatives + tighten PMax signals</h4><p>Non-brand search is leaking on “cheap sneakers online” (0.9×). Negatives + better audience signals should push PMax past 4×.</p></div>
    </div>

    <div class="card" style="margin-top:20px;padding:16px 20px;display:flex;justify-content:space-between;flex-wrap:wrap;gap:10px;align-items:center">
      <div><b>Projected July blended ROAS</b> <span class="muted">at approved changes</span></div>
      <div style="font-size:22px;font-weight:800;color:var(--accent)">4.6× <span class="chip up" style="vertical-align:middle">▲ 10%</span></div>
    </div>
    <div class="faint" style="margin-top:26px;font-size:12.5px">Prepared by AdUp · drafted by TARA · 1 July 2026 · figures are simulated demo data.</div>
  </div>
</section>

<div class="pager">Acme B.V. · June 2026</div>
</body>
</html>`;
